import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDropzone } from 'react-dropzone';
import { debounce } from 'lodash';
import {
  Box,
  Button,
  Chip,
  IconButton,
  OutlinedInput,
  Typography,
  Zoom,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ErrorIcon from '@mui/icons-material/Error';
import HelpCircleMUI6 from 'src/components/HelpCircleMUI6';
import AiDraftFeedback from 'src/views/management/ChatView/NewMessage/AiDraftFeedback';
import AiOpsNotes from 'src/views/management/ChatView/NewMessage/AiOpsNotes';
import AiResearchNote from 'src/views/management/ChatView/NewMessage/AiResearchNote';
import AiLookup from 'src/views/management/ChatView/NewMessage/AiLookup';
import AiResearchNoteRestore from 'src/views/management/ChatView/NewMessage/AiResearchNoteRestore';
import InputStartAdornment from 'src/views/management/ChatView/NewMessage/InputStartAdornment';
import VoiceRecorder, { isRecordingSupported } from 'src/components/VoiceRecorder/VoiceRecorder';
import ModalDialogMUI6 from 'src/components/ModalDialogMUI6';
import { fetchVoiceMemoDisclosure, acknowledgeVoiceMemoDisclosure } from 'src/actions/accountActions';
import InputEndAdornment from 'src/views/management/ChatView/NewMessage/InputEndAdornment';
import ModalScheduleMessage from 'src/views/management/ChatView/modals/ModalScheduleMessage';
import ModalInsertDynamicFields from 'src/views/management/ChatView/modals/ModalInsertDynamicFields';
import ModalApplyTemplate from 'src/views/management/ChatView/modals/ModalApplyTemplate';
import ModalInsertSmartLink from 'src/views/management/ChatView/modals/ModalInsertSmartLink';
import ModalInsertSmartForm from 'src/views/management/ChatView/modals/ModalInsertSmartForm';
import PhoneSelector from 'src/views/management/ChatView/PhoneSelector';
import SendToPhone from 'src/views/management/ChatView/SendToPhone';
import { buildContactPhoneList } from 'src/views/management/ChatView/contactPhones';
import AttachedFilePrewiew from 'src/views/management/ChatView/NewMessage/AttachedFilePrewiew';
import AttachmentPendingTile from 'src/components/AttachmentPendingTile';
import CharacterCounter from 'src/views/management/ChatView/styled/CharacterCounter';
import TooltipMUI6Container from 'src/components/TooltipMUI6/TooltipMUI6Container';
import {
  addMessage,
  clearFilteredChatMessages,
  clearSystemGenMessageReplacedFlag,
  deleteDraft,
  DELETE_DRAFT_SUCCESS,
  replaceSystemGenMessage,
  saveDraft,
  setFileUploadToMessage,
} from 'src/actions/chatActions';
import { addNotification } from 'src/actions/notificationActions';
import {
  PERMISSIONS,
  NOTIFICATION_STATUS,
  STORAGE,
  STYLE_OPTIONS,
  MESSAGES,
} from 'src/constants';
import helpers from 'src/helpers';
import { compressImageIfNeeded, isCompressibleImage } from 'src/utils/imageCompression';
import { MAX_RECORDING_SIZE_BYTES } from 'src/helpers/azure';
import {
  planNewFileTargets, sumAttachmentBytes, MIXED_CONTAINER_MESSAGE, tooManyFilesMessage, ATTACHMENTS_BUSY_MESSAGE,
} from 'src/helpers/attachmentBudget';
import {
  canAddContactCard,
  containerForFile,
  templateDropsAttachments,
  TEMPLATE_REPLACED_ATTACHMENTS_MESSAGE,
  templateAttachmentsForApply,
} from 'src/helpers/contactCardAttachments';
import { rebalanceAttachments } from 'src/helpers/attachmentRebalance';
import EnterKeySettings from './EnterKeySettings';
import useEnterKeyPreference from './useEnterKeyPreference';
import {
  getEnterKeyHint,
  isMacPlatform,
  isTouchDevice,
  shouldSendOnKeyDown,
} from './enterKeyPreference';
import {
  buildDraftAttachmentEntry, draftStateKey, isRecordedAttachment, recordedAttachmentFromDraft,
} from './draftAttachments';

const VALIDATE_MESSAGE = {
  validateFileSize: 'File size must be less than 300Kb',
  validateFileType: 'Must be a jpg, jpeg, png, gif, mp4, mp3, webm, ogg, wav, flv, mov, wmv, avi, mkv, 3gp, flac, amr, aac, wma, m4a, pdf, doc, docx, txt, rtf, vcf, zip, rar, or 7z file',
};

const { PERMISSIONS_SYSTEM_CODE } = PERMISSIONS;
const { VCARD_FILENAME } = MESSAGES;

/* CON-1929 — every image on the clipboard, as Files, for the multi-attachment
 * path. Nothing is compressed here: the pasted images go through exactly the
 * add path a picked file takes (limits, budget, rebalance, refusals). Returns
 * false when the clipboard holds no image, so ordinary text paste is left
 * alone. */
const readClipboardImages = (event) => {
  const items = Array.from(event.clipboardData?.items || []);
  const imageItems = items.filter((item) => item.type.indexOf('image') !== -1);
  if (!imageItems.length) return false;
  event.preventDefault();

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
  if (imageItems.some((item) => !allowedTypes.includes(item.type))) {
    return { success: false, error: VALIDATE_MESSAGE.validateFileType, files: [] };
  }
  const timestamp = new Date().getTime();
  const files = imageItems
    .map((item) => item.getAsFile())
    .filter(Boolean)
    .map((file, index) => {
      const extension = file.type.split('/')[1];
      const suffix = imageItems.length > 1 ? `-${index + 1}` : '';
      return new File([file], `pasted-image-${timestamp}${suffix}.${extension}`, { type: file.type });
    });
  return { success: true, error: null, files };
};

const handleClipboardPaste = async (event, onImagePasted) => {
  const items = event.clipboardData?.items;
  if (!items) return false;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    // Check if the item is an image
    if (item.type.indexOf('image') !== -1) {
      event.preventDefault();

      const file = item.getAsFile();
      if (!file) return false;

      try {
        // Check file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
          return {
            success: false,
            error: VALIDATE_MESSAGE.validateFileType,
          };
        }

        // Compress if needed (uses 300KB target)
        // eslint-disable-next-line no-await-in-loop
        const compressionResult = await compressImageIfNeeded(file);

        if (!compressionResult.success) {
          return {
            success: false,
            error: compressionResult.error,
          };
        }

        // Create filename for pasted image
        const timestamp = new Date().getTime();
        const extension = file.type.split('/')[1];
        const fileName = `pasted-image-${timestamp}.${extension}`;

        // Create file upload object with compressed file
        const processedFile = new File([compressionResult.file], fileName, { type: file.type });
        const uploadFileData = {
          file: processedFile,
          value: fileName,
        };

        onImagePasted(uploadFileData);

        return {
          success: true,
          file: uploadFileData,
        };
      } catch (error) {
        console.error('Error processing pasted image:', error);
        return {
          success: false,
          error: 'Failed to process pasted image',
        };
      }
    }
  }

  return false;
};

function MessageAdd({
  children = null,
  chat,
  maxTextMessageLength,
  showScrollButton = false,
  userRolePermissions = [],
  setShowScheduledMessages,
  scrollMessagesToBottom,
  sendingLimits,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const [manuallySelectedNumber, setManuallySelectedNumber] = useState('');
  // Bumped whenever the research note's dismissed state changes. The note has
  // two views - the panel above the compose box and the retrieval icon in the
  // toolbar group - each holding its own copy, so without a shared signal
  // dismissing would leave the way back invisible until a reload.
  //
  // Paired with aiSignalVersion into a COMPOSITE refreshKey, never a sum. The
  // two counters move independently and aiSignalVersion resets to 0 on a
  // contact switch, so a sum can land back on a value it already had and the
  // refresh silently does not fire.
  const [researchNoteVersion, setResearchNoteVersion] = useState(0);
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [insertDynamicModalVisible, setInsertDynamicModalVisible] = useState(false);
  const [applyTemplateModalVisible, setApplyTemplateModalVisible] = useState(false);
  const [insertSmartLinkModalVisible, setInsertSmartLinkModalVisible] = useState(false);
  const [insertSmartFormModalVisible, setInsertSmartFormModalVisible] = useState(false);
  // CON-1587 — CP Page insert modal (its own menu item; reuses ModalInsertSmartForm).
  const [insertCpPageModalVisible, setInsertCpPageModalVisible] = useState(false);
  const [messageCursorPosition, setMessageCursorPosition] = useState(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  // CON-1674 — isRecording drives the recorder UI. recordedAttachment is the
  // EXACT blob name the recorder uploaded; whether the current attachment is a
  // voice memo is derived from it (isRecordedAttachment), never stored. See
  // draftAttachments.js for why — a stored boolean drifted from the attachment
  // three times.
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAttachment, setRecordedAttachment] = useState(null);
  // validateFileInput is declared further down; the recorder handler above it
  // reaches the current one through this ref rather than reordering the file.
  const validateFileInputRef = useRef(null);
  // CON-1929 — the original File behind every blob picked or pasted in this
  // session, keyed by blob name, so a rebalance recompresses from the
  // original rather than from an already-shrunk copy. Anything missing here
  // (a restored draft) is fetched back from its blob instead.
  const originalsRef = useRef(new Map());
  /* CON-1930 — beta only: while a pick or paste is being compressed and
   * uploaded, pendingTiles placeholders stand in the attachment row, Send
   * and the + menu are disabled, and another pick or paste is turned away
   * (addingFilesRef answers at once; state alone would let a fast double
   * paste through before React re-renders). */
  const [pendingTiles, setPendingTiles] = useState(0);
  const addingFilesRef = useRef(false);
  // CON-1674 — the charge disclosure. Connections is the ONLY surface that
  // carries it: everything else that can record already charges the automated
  // per-text rate and users accept that, so a notice there would restate a
  // charge that is not new. Here it IS new — everything sent from Connections
  // has been free.
  // null = NOT YET KNOWN. Initialising to true would let a first-time user who
  // opens the + menu before the fetch resolves record with no notice and be
  // charged — exactly the case the fail-open exists to prevent. The menu item
  // is withheld until this resolves, and anything other than an explicit true
  // shows the notice.
  const [voiceMemoAcknowledged, setVoiceMemoAcknowledged] = useState(null);
  const [showVoiceMemoDisclosure, setShowVoiceMemoDisclosure] = useState(false);
  // Ref to track if draft was loaded from server (to avoid saving it back immediately)
  const draftLoadedRef = useRef(false);
  const lastSavedDraftRef = useRef('');
  // Track the contact ID for which we've already loaded the draft
  const draftLoadedForContactRef = useRef(null);
  // Track the contact ID whose server draft has been hydrated into the input.
  // Separate from draftLoadedForContactRef: on a deep link / hard refresh the
  // contact is ready before the chat list (which carries the draft) has
  // loaded, so hydration must wait for the Redux entry without blocking
  // autosave in the meantime.
  const draftHydratedForContactRef = useRef(null);

  const {
    user,
    azAccountName,
    databaseBeingUsed,
    mmsAttachmentPolicy,
  } = useSelector((state) => state.account);
  const { customer: contact } = useSelector((state) => state?.customerDetails || {});
  const {
    chatsContacts,
    currentMessage,
    followUpMessages,
    isMessageSending,
    isMessageUpdating,
    messagesFilteredByPhone,
    aiDraftSignal,
  } = useSelector((state) => state?.chat || {});

  // CON-1673. The version of the last AI draft/note signal that was for THIS
  // contact. Zero when none has arrived, so an event for a different chat can
  // never re-hydrate this one.
  const aiSignalVersion = useMemo(() => (
    aiDraftSignal?.contactId && String(aiDraftSignal.contactId) === String(contact?.id)
      ? aiDraftSignal.version || 0
      : 0
  ), [aiDraftSignal, contact?.id]);

  // Get chat preferences including draft from chatsContacts
  const chatPreferences = useMemo(() => {
    const contactId = contact?.id;
    if (!contactId) return {};
    return chatsContacts?.byId?.[String(contactId)]?.chatPreferences || {};
  }, [contact?.id, chatsContacts]);

  const {
    draftMessage, draftAttachments, draftSource, draftTemplate,
  } = chatPreferences;

  // Label for the template chip: which message template an AI draft came from,
  // e.g. "usr040: Getting Started Guides". Description is snapshotted server
  // side, so the chip never depends on the template list being loaded.
  const templateChipLabel = useMemo(() => {
    const code = draftTemplate?.code;
    if (!code) return null;
    const description = String(draftTemplate?.description || '').trim();
    if (!description) return code;
    const shortened = description.length > 28
      ? `${description.slice(0, 28).trim()}...`
      : description;
    return `${code}: ${shortened}`;
  }, [draftTemplate]);

  const {
    isDailySMSSendingDisabled,
    serverTimeWithWait,
  } = sendingLimits || {};

  const isSMSSendingDisabled = !!isDailySMSSendingDisabled;

  const {
    fileUpload = false,
    systemGenMessageReplaced,
  } = currentMessage || {};
  const { dynamicFields } = user || {};
  const { followUpMessageList } = followUpMessages || {};

  const lastChatMessage = chat.messages.at(-1);
  // CON-1705 — same helper as the Toolbar filter, so the two selectors on this
  // screen always agree about which phone is at which index.
  const {
    phones: contactPhones,
    phoneInfoList: contactPhoneInfoList,
  } = buildContactPhoneList(contact);
  const isPhonesListEmpty = !!contact?.id && helpers.isEmpty(contactPhones);

  /* CON-1925 — how many files this message may carry. Outside the beta
   * (no MULTI_ATTACHMENT_MMS permission, or not on CP texting) it is exactly
   * one, extraAttachments stays empty, and nothing below behaves differently
   * from before. The first attachment keeps living in values.attachment +
   * values.messageAttachmentContainer so every existing path (voice memo,
   * contact card, send button, drafts) is untouched; the 2nd..Nth live in
   * values.extraAttachments, blob names in the SAME container. */
  const canSendMultiple = helpers.permissions.canSendMultipleAttachments(user);
  const maxAttachments = canSendMultiple
    ? Math.max(1, Number(mmsAttachmentPolicy?.maxAttachments) || 1)
    : 1;
  const mmsTotalBytes = Number(mmsAttachmentPolicy?.totalBytes) || 307200;

  const { getInputProps, open: handleOpenAttachFile } = useDropzone({
    maxFiles: maxAttachments,
    multiple: maxAttachments > 1,
    noClick: true,
    noKeyboard: true,
    noDrag: true,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'video/mp4': ['.mp4'],
      'audio/mpeg': ['.mp3'],
      'video/webm': ['.webm'],
      'audio/ogg': ['.ogg'],
      'audio/wav': ['.wav'],
      'video/x-flv': ['.flv'],
      'video/flv': ['.flv'],
      'video/quicktime': ['.mov'],
      'video/mov': ['.mov'],
      'video/x-ms-wmv': ['.wmv'],
      'video/wmv': ['.wmv'],
      'video/x-msvideo': ['.avi'],
      'video/avi': ['.avi'],
      'video/msvideo': ['.avi'],
      'video/x-matroska': ['.mkv'],
      'video/3gpp': ['.3gp'],
      'video/3gp': ['.3gp'],
      'audio/flac': ['.flac'],
      'audio/amr': ['.amr'],
      'audio/amr-wb': ['.amr'],
      'audio/x-amr': ['.amr'],
      'audio/aac': ['.aac'],
      'audio/vnd.dlna.adts': ['.aac'],
      'application/octet-stream': ['.amr', '.aac', '.m4a'],
      'audio/mp4': ['.m4a'],
      'audio/x-m4a': ['.m4a'],
      'audio/x-ms-wma': ['.wma'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'application/rtf': ['.rtf'],
      'text/vcard': ['.vcf'],
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
      'application/x-rar-compressed': ['.rar'],
      'application/vnd.rar': ['.rar'],
      'application/x-rar': ['.rar'],
      'application/rar': ['.rar'],
      'application/x-7z-compressed': ['.7z']
    },
  });

  const getLastMessagePhoneNumber = () => {
    if (lastChatMessage && contactPhones.includes(lastChatMessage?.phone)) {
      return lastChatMessage.phone;
    }
    return null;
  };

  // Extract draft attachment data (format: [{ filename, container }])
  const draftAttachment = draftAttachments?.[0];

  // Don't include draftMessage/draftAttachment in dependencies to prevent
  // Formik reinitialization (and cursor reset) when drafts auto-save
  const initialValues = useMemo(() => ({
    contactId: contact.id,
    number: manuallySelectedNumber || getLastMessagePhoneNumber() || contact?.phone1 || contact?.phone2 || contact?.phone3,
    message: '',
    attachment: null,
    messageAttachmentContainer: null,
    extraAttachments: [],
    scheduledDateTime: null,
  }), [chat.key, lastChatMessage, contact, manuallySelectedNumber]);

  const validationSchema = Yup.object().shape({
    number: Yup.string(),
    message: Yup.string().max(maxTextMessageLength),
    scheduledDateTime: Yup.date('Invalid date')
      .nullable(true)
      .typeError('Invalid date')
      .min(
        isDailySMSSendingDisabled
          ? serverTimeWithWait.clone().local().toDate()
          : new Date(),
        isDailySMSSendingDisabled
          ? `You have reached your daily text message limit. Please choose a date after ${serverTimeWithWait.clone().local().format('MMM\xa0D, h:mm\xa0a')}`
          : 'Date must be in the future'
      )
      .max(new Date(2100, 1, 1, 0, 0, 0), 'Date must be before 2100'),
    attachment: Yup.string()
      .nullable()
      .test('invalidAttachmentSize', VALIDATE_MESSAGE.validateFileSize, () => helpers.azure.validateFileSizeForForm(fileUpload))
      .test('invalidAttachmentType', VALIDATE_MESSAGE.validateFileType, () => helpers.azure.validateFileType(fileUpload)),
  });

  const fileWasNotUpload = () => {
    dispatch(addNotification('file upload', 'Your file wasn\'t uploaded', NOTIFICATION_STATUS.ERROR));
  };

  const fileWasNotDelete = () => {
    dispatch(addNotification('file delete', 'Your file wasn\'t deleted', NOTIFICATION_STATUS.ERROR));
  };

  const handleSetFocusField = useCallback(() => {
    const inputElements = document.getElementsByName('message');
    if (inputElements.length > 0) {
      inputElements[0].focus(); // {preventScroll: true} doesn't work

      if (typeof messageCursorPosition === 'number') {
        // Set the cursor position for input
        inputElements[0].setSelectionRange(messageCursorPosition, messageCursorPosition);
      }
    }
  }, [messageCursorPosition]);

  // Debounced save draft function. Declared before onSubmit, which must be
  // able to cancel a pending save at send-click.
  const debouncedSaveDraft = useMemo(
    () => debounce((contactId, messageText, attachmentData, extraAttachments = []) => {
      if (!contactId) return;

      const message = messageText || '';
      const trimmedForCheck = message.trim();
      // CON-1694 — the recorded marker rides WITH the attachment. See
      // draftAttachments.js for why it is carried rather than inferred.
      const attachmentsToSave = [];
      const draftEntry = buildDraftAttachmentEntry(attachmentData || {});
      if (draftEntry) {
        attachmentsToSave.push(draftEntry);
      }
      // CON-1925 — the 2nd..Nth files, one entry each, same container, never
      // a memo (a memo is always sent alone).
      (extraAttachments || []).forEach((filename) => {
        const extraEntry = buildDraftAttachmentEntry({
          attachment: filename,
          container: attachmentData?.container,
          isVoiceMemo: false,
        });
        if (extraEntry) {
          attachmentsToSave.push(extraEntry);
        }
      });

      // Create a key to compare current state with last saved
      const currentStateKey = draftStateKey(message, attachmentsToSave);

      // Only save if different from last saved value
      if (currentStateKey === lastSavedDraftRef.current) return;

      lastSavedDraftRef.current = currentStateKey;

      if (trimmedForCheck || attachmentsToSave.length > 0) {
        dispatch(saveDraft(contactId, {
          draftMessage: message,
          draftAttachments: attachmentsToSave,
        }));
      } else {
        // Clear draft if message and attachments are empty
        dispatch(deleteDraft(contactId));
      }
    }, 1000),
    [dispatch]
  );

  const onSubmit = async (values, {
    resetForm,
    setErrors,
    setStatus,
    setSubmitting,
    setFieldValue
  }) => {
    setSubmitting(true);

    // Kill the autosave race before the send goes out: the successful send
    // resets the form, the autosave effect sees the now-empty box, and ~1s
    // later would fire deleteDraft while the send POST is still inside the
    // provider call - the server would mark a pending AI draft discarded
    // before send-capture records it. Cancel any pending save and teach the
    // ref that "empty" is already saved, so no delete gets scheduled. Keep
    // the pre-send key to restore if the send fails (the draft still exists
    // then, and a manual clear must still work).
    const preSendDraftKey = lastSavedDraftRef.current;
    if (values.contactId) {
      debouncedSaveDraft.cancel();
      lastSavedDraftRef.current = draftStateKey('', []);
    }

    const doWhenTry = (isScheduled) => {
      if (!isScheduled) {
        setShowScheduledMessages(false);
      }
      dispatch(setFileUploadToMessage(false));
      resetForm();
      setStatus({ success: true });
      setSubmitting(false);
      scrollMessagesToBottom();
      handleSetFocusField();

      // Clear the draft after successful send
      if (values.contactId) {
        if (isScheduled) {
          // Scheduled sends don't clear the server-side draft (only the
          // instant-send controller does), so the API call is still needed
          dispatch(deleteDraft(values.contactId));
        } else {
          // Instant sends: the server already cleared the draft inline and
          // send-capture recorded the outcome. Only sync the Redux copy -
          // re-hitting the delete endpoint is what raced send-capture and
          // mislabeled sent drafts as discarded.
          dispatch({
            type: DELETE_DRAFT_SUCCESS,
            payload: {
              contactId: String(values.contactId),
              chatPreferences: {
                draftMessage: null,
                draftAttachments: null,
                draftTemplate: null,
                draftSource: null,
              },
            },
          });
        }
      }
    };
    const doWhenCatch = (error, errorMessage) => {
      setStatus({ success: false });
      setErrors({ submit: errorMessage || error?.message || 'Try again later' });
      setSubmitting(false);
      if (!helpers.isEmpty(values.scheduledDateTime)) {
        setFieldValue('scheduledDateTime', null);
      }
      // Send failed: the draft still exists, so restore the ref - autosave
      // and manual clears must keep working against the real saved state
      lastSavedDraftRef.current = preSendDraftKey;
    };

    const newValuesAttachment = await helpers.azure.loadFileToAzureAndGetStatusCode(fileUpload, values.attachment, user.id, azAccountName, STORAGE.sas, databaseBeingUsed, values.messageAttachmentContainer, fileWasNotUpload, fileWasNotDelete, false, 'EO-ID');
    if (newValuesAttachment === null) {
      dispatch(addNotification('saveFile', 'There are some problems with Azure service.', NOTIFICATION_STATUS.ERROR));
      return;
    }

    /* CON-1674 — REFUSE rather than silently sending without the attachment.
     *
     * values.attachment is what the compose box renders its attachment card
     * from, so a non-empty value means the user can SEE an attachment. If the
     * upload helper hands back an empty string for it, the send path has just
     * discarded something visible on screen — and the only way the message can
     * proceed is as text-only, with nothing telling the user the attachment
     * was dropped. That is exactly how a voice memo went out as a bare text.
     *
     * There is no legitimate path into this state: a real removal clears
     * values.attachment first (updateFileUploadToMessage), and a replacement
     * returns the new file name. So an empty result against a visible
     * attachment means form state and the send path disagree, and the correct
     * answer is to stop rather than to guess which one the user meant. */
    if (!helpers.isEmpty(values.attachment) && helpers.isEmpty(newValuesAttachment)) {
      dispatch(addNotification(
        'attachment-dropped',
        'That attachment could not be sent. Nothing was sent — please attach it again and retry.',
        NOTIFICATION_STATUS.ERROR,
      ));
      return;
    }

    const messageAttachmentContainer = helpers.isEmpty(newValuesAttachment)
      ? null
      : values.messageAttachmentContainer || helpers.azure.getUserContainerName(databaseBeingUsed, user.id);

    const isScheduledMessage = !helpers.isEmpty(values.scheduledDateTime);

    const sendData = {
      contactId: values.contactId,
      phone: values.number,
      body: values.message,
      attachment: newValuesAttachment,
      // CON-1925 — present ONLY when there is more than one file, so a
      // single-attachment send is byte-for-byte the request it always was.
      ...(values.extraAttachments?.length
        ? { attachments: [newValuesAttachment, ...values.extraAttachments] }
        : {}),
      messageAttachmentContainer,
      // CON-1674 — set ONLY when the attachment being sent IS the file the
      // recorder produced. An .mp3 from disk, a photo attached after a memo was
      // sent, anything else: false, without anything having to clear it.
      isVoiceMemo: isRecordedAttachment(values.attachment, recordedAttachment) && !!newValuesAttachment,
      scheduledAt: !helpers.isEmpty(values.scheduledDateTime) ? moment(values.scheduledDateTime).utc() : null,
    };
    dispatch(addMessage(isScheduledMessage, sendData, chat.key, doWhenTry, doWhenCatch));
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const {
    values,
    errors,
    status,
    isSubmitting,
    handleChange,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
  } = formik;

  // Derived, not stored — see isRecordedAttachment. Read by autosave and by the
  // "Charged at your automated message rate." reminder, so the disclosure and
  // the charge cannot disagree with the attachment either.
  const isVoiceMemo = isRecordedAttachment(values.attachment, recordedAttachment);

  const isLoading = isSubmitting || isMessageSending || isMessageUpdating;
  const canCreateMessage = helpers.permissions.doesUserHavePermission(PERMISSIONS_SYSTEM_CODE.CHAT_MESSAGE_CREATE, userRolePermissions);
  const canViewSmartLinks = helpers.permissions.doesUserHavePermission(PERMISSIONS_SYSTEM_CODE.SMART_LINKS_VIEW, userRolePermissions);
  const canViewSmartForms = helpers.permissions.doesUserHavePermission(PERMISSIONS_SYSTEM_CODE.SMART_FORMS_VIEW, userRolePermissions);
  const isMessageIncludesBlanks = values?.message && values.message?.includes('__________');

  // Auto-save draft when message or attachment changes
  useEffect(() => {
    // Skip saving until draft has been loaded for this contact
    if (!draftLoadedRef.current) {
      return undefined;
    }

    // Only save if we have a contact
    if (contact?.id) {
      const attachmentFilename = values.attachment || null;
      const attachmentContainer = values.messageAttachmentContainer || null;

      debouncedSaveDraft(contact.id, values.message, {
        attachment: attachmentFilename,
        container: attachmentContainer,
        isVoiceMemo,
      }, values.extraAttachments);
    }

    // Cleanup on unmount
    return () => {
      debouncedSaveDraft.cancel();
    };
  }, [values.message, values.attachment, values.messageAttachmentContainer, values.extraAttachments, isVoiceMemo, contact?.id, debouncedSaveDraft]);

  /* CON-1674 — set an attachment that is ALREADY uploaded to blob storage.
   *
   * THE SENTINEL, WHICH IS LOAD-BEARING AND WAS UNDOCUMENTED. Redux
   * `currentMessage.fileUpload` is not merely "the pending file". Its value
   * carries meaning that the SEND path reads back:
   *
   *     undefined   no attachment action taken this session
   *     null        the user DELETED the attachment  <- AttachedFilePrewiew's x
   *     false       nothing pending (an attachment is set by name)
   *     { file }    a File waiting to be uploaded at send time
   *
   * loadFileToAzureAndGetStatusCode reads it as:
   *
   *     const isFileDeleted = (fileUpload === null || fileUpload) && !isEmpty(attachmentStr);
   *
   * so `null` means "delete the blob named by values.attachment and return ''".
   * Any writer that puts a filename into values.attachment MUST therefore clear
   * the sentinel too. A writer that sets only Formik leaves a previous discard's
   * `null` in place, and at send time that discard deletes the NEW attachment
   * and sends the message without it — silently, because the helper reports
   * success. That is the discard-then-re-record bug.
   *
   * `false` rather than undefined is deliberate: it is what every other writer
   * here already dispatches, and it keeps "cleared" distinguishable from
   * "untouched". */
  const setUploadedAttachment = useCallback((fileName, containerName) => {
    setFieldValue('attachment', fileName, false);
    setFieldValue('messageAttachmentContainer', containerName, false);
    dispatch(setFileUploadToMessage(false));
  }, [dispatch, setFieldValue]);

  // Load draft when contact changes (separate from initialValues to prevent cursor jumping)
  // Using refs to track contact ID ensures we only load the draft once per contact
  // switch, not when draft data updates from Redux (which would cause cursor to jump).
  // Hydration is gated on the contact's chat-list entry existing in Redux: on a deep
  // link / hard refresh this effect first runs before getChatList has responded, and
  // stamping "loaded" at that point silently dropped the server draft (found via the
  // AI Support Drafts prefill, but it affected user-typed drafts the same way).
  useEffect(() => {
    if (!contact?.id) return;

    // First pass for this contact: enable autosave and reset hydration state
    if (draftLoadedForContactRef.current !== contact.id) {
      draftLoadedForContactRef.current = contact.id;
      draftHydratedForContactRef.current = null;
      draftLoadedRef.current = true;
      lastSavedDraftRef.current = draftStateKey('', []);
    }

    // Hydrate once per contact, and only after the chat-list entry carrying the
    // draft has actually loaded.
    //
    // The entry EXISTING is not the same as its draft having arrived. getChatList
    // always sends a chatPreferences object (a defaults object when the contact
    // has no row), but the entry can also be created or merged by other paths -
    // GET_CUSTOMER_SUCCESS and the paged Object.assign in chatReducer - which
    // carry no preferences at all. Claiming hydration then consumed this
    // one-shot guard with nothing to hydrate from, and the draft never appeared
    // for that contact: no retry, because the ref already names it.
    //
    // The symptom is an empty compose box with an intact draftMessage row in the
    // database, which reads to the support person as "no draft was generated".
    // Waiting for chatPreferences rather than for the entry closes that.
    const chatListEntry = chatsContacts?.byId?.[String(contact.id)];
    const draftPreferencesLoaded = Boolean(chatListEntry)
      && chatListEntry.chatPreferences !== undefined;
    // CON-1673. The hydration key carries the AI signal version, not just the
    // contact id. Before this it was one-shot per contact, so even once the
    // store was refreshed over the socket the compose box would not pick a
    // draft up - the ref already named the contact. Bumping the version makes
    // the key stop matching exactly once per arriving draft, and no more.
    const hydrationKey = `${contact.id}:${aiSignalVersion}`;
    if (!draftPreferencesLoaded || draftHydratedForContactRef.current === hydrationKey) return;
    draftHydratedForContactRef.current = hydrationKey;

    // Never overwrite text the user has already typed while the list was
    // loading - and, since CON-1673, never overwrite what she is typing RIGHT
    // NOW when a draft arrives over the socket mid-thought. This is the only
    // gate on that path and it is deliberately the same one: the server has
    // already refused to write to a box she owns (draftSource cleared by her
    // first keystroke), so a draft she is typing over cannot even be
    // announced, and if one somehow were, this still declines it.
    const userHasTyped = Boolean(values.message?.trim() || values.attachment);
    if (!userHasTyped) {
      // Load draft message if available
      if (draftMessage) {
        setFieldValue('message', draftMessage, false);
      }

      // Load draft attachment if available
      if (draftAttachment?.filename) {
        // Same sentinel rule as the recorder — see setUploadedAttachment. A
        // draft restored after a discard would otherwise be deleted at send.
        setUploadedAttachment(draftAttachment.filename, draftAttachment.container || null);

        /* CON-1694 — restore the recorded marker with the attachment.
         *
         * This drives three things at once, which is why losing it was worse
         * than a billing miss: the send payload's isVoiceMemo (so the server
         * transcodes to a playable MP3 and picks the charging send method), and
         * the "Charged at your automated message rate." reminder under the
         * compose box. Restoring the flag without the reminder would charge a
         * consultant with no notice on screen, which is the thing the
         * disclosure exists to prevent.
         *
         * Drafts saved BEFORE CON-1694 have no isVoiceMemo key and restore as
         * ordinary attachments: we cannot know what they were, and guessing
         * would risk charging for an upload. A draft that carries the marker on
         * a file that is not audio — a stuck marker persisted before the marker
         * was derived — has it dropped here; see recordedAttachmentFromDraft. */
        setRecordedAttachment(recordedAttachmentFromDraft(draftAttachment));

        /* CON-1925 — the 2nd..Nth draft files come back too, but only for a
         * user who is still in the beta and only up to the current ceiling.
         * Anything beyond that is left behind rather than sent, which is the
         * fail-safe direction. */
        const extraDraftNames = canSendMultiple
          ? (draftAttachments || []).slice(1, maxAttachments)
            .map((entry) => entry?.filename)
            .filter(Boolean)
          : [];
        setFieldValue('extraAttachments', extraDraftNames, false);
      }

      // Initialize the lastSavedDraftRef so we don't immediately re-save.
      // Normalised through the SAME function the autosave comparison uses —
      // see draftStateKey. Seeding this from the raw server draft made every
      // pre-existing draft mismatch and fire one extra write per switch.
      lastSavedDraftRef.current = draftStateKey(draftMessage, draftAttachments);
    }
  }, [
    contact?.id, chatsContacts, draftMessage, draftAttachment, draftAttachments,
    aiSignalVersion, setFieldValue,
  ]);

  const saveMessageCursorPosition = (event) => {
    setMessageCursorPosition(event?.target?.selectionStart);
  };

  const handleOpenPopover = (event) => {
    setPopoverAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setPopoverAnchorEl(null);
  };

  const handleOpenScheduledModal = () => {
    // Set default scheduled time to 5 minutes from now for better UX
    setFieldValue('scheduledDateTime', moment().add(5, 'minutes'));
    setScheduleModalVisible(true);
    handleClosePopover();
  };

  const handleCloseScheduledModal = () => {
    setScheduleModalVisible(false);
    handleClosePopover();
  };

  const handleOpenDynamicFieldModal = () => {
    setInsertDynamicModalVisible(true);
    handleClosePopover();
  };

  const handleCloseDynamicFieldModal = () => {
    setInsertDynamicModalVisible(false);
    handleClosePopover();
  };

  const handleOpenMessageTemplateModal = () => {
    setApplyTemplateModalVisible(true);
    handleClosePopover();
  };

  const handleCloseMessageTemplateModal = () => {
    setApplyTemplateModalVisible(false);
    handleClosePopover();
  };

  const handleOpenSmartLinkModal = () => {
    setInsertSmartLinkModalVisible(true);
    handleClosePopover();
  };

  const handleCloseSmartLinkModal = () => {
    setInsertSmartLinkModalVisible(false);
    handleClosePopover();
  };

  const handleOpenSmartFormModal = () => {
    setInsertSmartFormModalVisible(true);
    handleClosePopover();
  };

  const handleCloseSmartFormModal = () => {
    setInsertSmartFormModalVisible(false);
    handleClosePopover();
  };

  const handleOpenCpPageModal = () => {
    setInsertCpPageModalVisible(true);
    handleClosePopover();
  };

  const handleCloseCpPageModal = () => {
    setInsertCpPageModalVisible(false);
    handleClosePopover();
  };

  /* CON-1934 — the contact card beside files: the new set is re-planned
   * against the budget like an add, the card a fixed file at its real size
   * (about 70 KB with a photo) and the pictures shrunk to share the rest.
   * Over budget, the budget message shows and nothing changes. */
  const applyCardWithFiles = async (names, containerName) => {
    if (addingFilesRef.current) {
      dispatch(addNotification('attachment-busy', ATTACHMENTS_BUSY_MESSAGE, NOTIFICATION_STATUS.INFO));
      return;
    }
    addingFilesRef.current = true;
    try {
      const ownContainer = helpers.azure.getUserContainerName(databaseBeingUsed, user.id);
      const result = await rebalanceAttachments({
        existing: names.map((name) => ({ name, original: originalsRef.current.get(name) })),
        newFiles: [],
        totalBytes: mmsTotalBytes,
        deps: {
          getUrl: (name) => helpers.azure.getImageFileURL(
            name,
            user.id,
            azAccountName,
            STORAGE.sas,
            databaseBeingUsed,
            containerForFile(name, containerName),
            true,
          ),
          upload: (file) => helpers.azure.loadFileToAzureAndGetStatusCode(
            { file, value: file.name },
            null,
            user.id,
            azAccountName,
            STORAGE.sas,
            databaseBeingUsed,
            ownContainer,
            fileWasNotUpload,
            fileWasNotDelete,
            false,
            'EO-ID',
            true,
          ),
        },
      });
      if (!result.ok) {
        dispatch(addNotification('attachment-size', result.error, NOTIFICATION_STATUS.ERROR));
        return;
      }
      result.originals.forEach((file, name) => originalsRef.current.set(name, file));
      const [head, ...rest] = result.names;
      setFieldValue('attachment', head, false);
      setFieldValue('extraAttachments', rest, false);
      setFieldValue('messageAttachmentContainer', containerName || ownContainer, false);
      dispatch(setFileUploadToMessage(false));
    } finally {
      addingFilesRef.current = false;
    }
  };

  const handleAttachContactCard = () => {
    // CON-1934 — room left: the card is ADDED in front and every file stays.
    // The card is the consultant's own file, so the set must be in their own
    // container; a template's file cannot share it (same rule as a pick).
    const extras = values.extraAttachments || [];
    if (!isVoiceMemo && canAddContactCard({ first: values.attachment || null, extras, maxAttachments })) {
      const ownContainer = helpers.azure.getUserContainerName(databaseBeingUsed, user.id);
      if (values.messageAttachmentContainer && values.messageAttachmentContainer !== ownContainer) {
        dispatch(addNotification('attachment-mixed', MIXED_CONTAINER_MESSAGE, NOTIFICATION_STATUS.ERROR));
        handleClosePopover();
        return;
      }
      handleClosePopover();
      applyCardWithFiles([VCARD_FILENAME, values.attachment, ...extras], ownContainer);
      return;
    }
    // CON-1934 — full, in the beta: the card replaces file 1 and the set is
    // re-planned with it at its real size.
    if (maxAttachments > 1 && !isVoiceMemo && extras.length) {
      handleClosePopover();
      applyCardWithFiles([VCARD_FILENAME, ...extras], values.messageAttachmentContainer);
      return;
    }
    // Set the attachment to the vCard filename - no container needed as it's fetched dynamically
    setFieldValue('attachment', VCARD_FILENAME, false);
    // CON-1925 — the card replaces the FIRST file only; any extra files stay
    // and still need their container (the card ignores it).
    setFieldValue(
      'messageAttachmentContainer',
      values.extraAttachments?.length ? values.messageAttachmentContainer : null,
      false,
    );
    // Clear any pending file upload from Redux
    dispatch(setFileUploadToMessage(false));
    handleClosePopover();
  };

  /* CON-1674 — the recorded memo takes exactly the same upload path as a
   * pasted or picked file: validate, push to Azure, set the form fields. The
   * ONLY difference is isVoiceMemo, which the send request carries and which
   * makes the server pick the charging send method. */
  const handleVoiceMemoRecorded = useCallback(async (file, meta) => {
    setIsRecording(false);

    /* The byte limit is designed never to fire — the 120 s cap always comes
     * first at any real bitrate — so when it does, something unexpected has
     * happened and the recording ended with no run-up. The time limit has the
     * amber countdown; this has nothing. Say so, and say nothing was lost. */
    if (meta?.stoppedBy === 'bytes') {
      dispatch(addNotification(
        'voice-memo',
        'Recording ended early because it reached its size limit. Everything up to that point was kept.',
        NOTIFICATION_STATUS.WARNING,
      ));
    }
    const uploadFileData = { file };

    // The RECORDING ceiling, not the audio-attachment one. The webm never
    // reaches a carrier; ffmpeg turns it into a 32 kbps MP3 first, and the
    // 120-second cap is what keeps that under the wire limit. Validating the
    // recording against MAX_AUDIO_SIZE_BYTES refused everything past ~31 s.
    if (validateFileInputRef.current
      && !validateFileInputRef.current(uploadFileData, { maxFileSize: MAX_RECORDING_SIZE_BYTES })) {
      return;
    }

    const containerName = helpers.azure.getUserContainerName(databaseBeingUsed, user.id);
    const uploadedFilename = await helpers.azure.loadFileToAzureAndGetStatusCode(
      uploadFileData,
      null,
      user.id,
      azAccountName,
      STORAGE.sas,
      databaseBeingUsed,
      containerName,
      fileWasNotUpload,
      fileWasNotDelete,
      false,
      'EO-ID',
    );

    if (uploadedFilename === null) {
      dispatch(addNotification('voice-memo', 'Failed to upload the recording.', NOTIFICATION_STATUS.ERROR));
      return;
    }

    setUploadedAttachment(uploadedFilename, containerName);
    // CON-1925 — a memo is always sent alone.
    setFieldValue('extraAttachments', [], false);
    setRecordedAttachment(uploadedFilename);
  }, [databaseBeingUsed, user?.id, azAccountName, dispatch, setUploadedAttachment, setFieldValue]);

  const handleVoiceMemoError = useCallback((message) => {
    setIsRecording(false);
    dispatch(addNotification('voice-memo', message, NOTIFICATION_STATUS.ERROR));
  }, [dispatch]);

  // `maxFileSize` overrides the type-based ceiling. The voice recorder passes
  // MAX_RECORDING_SIZE_BYTES because a recording goes to ffmpeg, not to the
  // carrier — see the comment on that constant. Everything else leaves it
  // unset and gets the ceiling for what it is.
  const validateFileInput = (uploadFileData, { maxFileSize = null } = {}) => {
    // Skip size validation for compressible images - compression will handle it
    const isImage = uploadFileData?.file && isCompressibleImage(uploadFileData.file);
    if (!isImage && !helpers.azure.validateFileSize(uploadFileData, maxFileSize)) {
      // CON-1674 — the message states the limit that was actually applied.
      // Audio gets 500,000 bytes, everything else 300 KB, so a fixed string
      // would now be wrong for one of them.
      dispatch(addNotification(
        'attachment-size',
        helpers.azure.fileSizeErrorMessage(uploadFileData?.file, maxFileSize),
        NOTIFICATION_STATUS.ERROR,
      ));
      return false;
    }
    if (!helpers.azure.validateFileType(uploadFileData)) {
      dispatch(addNotification('attachment-type', VALIDATE_MESSAGE.validateFileType, NOTIFICATION_STATUS.ERROR));
      return false;
    }
    return true;
  };

  validateFileInputRef.current = validateFileInput;

  // Read once per mount. Defaults to acknowledged=true so a slow or failed
  // request never flashes the modal at someone who has already accepted it;
  // the server read itself fails open in the other direction.
  useEffect(() => {
    let active = true;
    fetchVoiceMemoDisclosure().then((acknowledged) => {
      if (active) setVoiceMemoAcknowledged(acknowledged);
    });
    return () => { active = false; };
  }, []);

  const startVoiceMemo = useCallback(() => {
    handleClosePopover();
    // Only an explicit true skips the notice. null (unresolved) and false both
    // show it: being asked twice is a small annoyance, being charged without
    // having been told is not.
    if (voiceMemoAcknowledged === true) {
      setIsRecording(true);
      return;
    }
    setShowVoiceMemoDisclosure(true);
  }, [voiceMemoAcknowledged]);

  const confirmVoiceMemoDisclosure = useCallback(async () => {
    setShowVoiceMemoDisclosure(false);
    setVoiceMemoAcknowledged(true);
    setIsRecording(true);
    // Fire and forget: the consultant has accepted and is already recording.
    // A failure here only means they are asked once more next time.
    acknowledgeVoiceMemoDisclosure();
  }, []);

  const updateFileUploadToMessage = (value) => {
    /* CON-1925 — removing the first of several files promotes the next one.
     * Through setUploadedAttachment, NOT the null sentinel below: the sentinel
     * would make the send path delete the promoted blob (see the comment on
     * setUploadedAttachment). */
    const extras = values.extraAttachments || [];
    if (value === null && extras.length) {
      setUploadedAttachment(
        extras[0],
        values.messageAttachmentContainer
          || helpers.azure.getUserContainerName(databaseBeingUsed, user.id),
      );
      setFieldValue('extraAttachments', extras.slice(1), false);
      setRecordedAttachment(null);
      return;
    }
    dispatch(setFileUploadToMessage(value));
    setFieldValue('attachment', null, false);
    setFieldValue('messageAttachmentContainer', null, false);
    // Not needed for correctness — the derived marker is already false once
    // the attachment is gone — but there is no reason to remember a recording
    // the consultant has thrown away.
    setRecordedAttachment(null);
  };

  /* CON-1925 — remove one of the 2nd..Nth files. The first file and its
   * container are not touched. */
  const removeExtraAttachment = (index) => {
    const extras = values.extraAttachments || [];
    setFieldValue('extraAttachments', extras.filter((_, i) => i !== index), false);
  };

  /* CON-1925 — attach one or more picked (or pasted) files.
   *
   * Outside the beta maxAttachments is 1 and this is the old
   * handleOnSelectedFile: validate, upload with the default compression,
   * replace whatever was attached. In the beta, with something already
   * attached and room left, the files are ADDED: the message's total size
   * budget is split across the new images before anything is uploaded, so
   * the whole text still fits the carrier limit.
   *
   * Returns the number of files that went on the message (CON-1929: fewer
   * than were given when the limit cut the list), or false when none did. */
  const addFilesToMessageNow = async (files) => {
    const picked = Array.from(files || []).filter(Boolean);
    if (!picked.length) return false;

    const existing = [values.attachment, ...(values.extraAttachments || [])].filter(Boolean);
    const ownContainer = helpers.azure.getUserContainerName(databaseBeingUsed, user.id);
    const addMode = maxAttachments > 1 && existing.length > 0 && !isVoiceMemo;
    let filesToAdd = picked;

    if (addMode) {
      // Every file on one message shares one container. A template's
      // attachment points at its author's container, so it cannot share a
      // message with an upload of the consultant's own.
      if (values.messageAttachmentContainer && values.messageAttachmentContainer !== ownContainer) {
        dispatch(addNotification('attachment-mixed', MIXED_CONTAINER_MESSAGE, NOTIFICATION_STATUS.ERROR));
        return false;
      }
      const room = maxAttachments - existing.length;
      if (room <= 0) {
        dispatch(addNotification('attachment-count', tooManyFilesMessage(maxAttachments), NOTIFICATION_STATUS.ERROR));
        return false;
      }
      if (picked.length > room) {
        dispatch(addNotification('attachment-count', tooManyFilesMessage(maxAttachments), NOTIFICATION_STATUS.WARNING));
        filesToAdd = picked.slice(0, room);
      }
    } else if (picked.length > maxAttachments) {
      dispatch(addNotification('attachment-count', tooManyFilesMessage(maxAttachments), NOTIFICATION_STATUS.WARNING));
      filesToAdd = picked.slice(0, maxAttachments);
    }

    const uploads = filesToAdd.map((file) => ({ file, value: file.name }));
    if (uploads.some((uploadFileData) => !validateFileInput(uploadFileData))) {
      return false;
    }

    /* CON-1929 — two or more attachments on the message: re-plan the WHOLE
     * set and recompress whatever is now over its share, from the originals,
     * converting PNG to JPEG unless it has transparency. See
     * helpers/attachmentRebalance.js. One attachment keeps the path below. */
    const existingForPlan = addMode ? existing : [];
    if (maxAttachments > 1 && existingForPlan.length + filesToAdd.length >= 2) {
      const containerName = values.messageAttachmentContainer || ownContainer;
      const result = await rebalanceAttachments({
        existing: existingForPlan.map((name) => ({ name, original: originalsRef.current.get(name) })),
        newFiles: filesToAdd,
        totalBytes: mmsTotalBytes,
        deps: {
          getUrl: (name) => helpers.azure.getImageFileURL(
            name,
            user.id,
            azAccountName,
            STORAGE.sas,
            databaseBeingUsed,
            containerForFile(name, containerName),
            true,
          ),
          upload: (file) => helpers.azure.loadFileToAzureAndGetStatusCode(
            { file, value: file.name },
            null,
            user.id,
            azAccountName,
            STORAGE.sas,
            databaseBeingUsed,
            ownContainer,
            fileWasNotUpload,
            fileWasNotDelete,
            false,
            'EO-ID',
            true,
          ),
        },
      });
      if (!result.ok) {
        dispatch(addNotification('attachment-size', result.error, NOTIFICATION_STATUS.ERROR));
        return false;
      }
      result.originals.forEach((file, name) => originalsRef.current.set(name, file));
      const [firstName, ...restNames] = result.names;
      if (firstName !== values.attachment) {
        setUploadedAttachment(firstName, ownContainer);
      } else if (!values.messageAttachmentContainer) {
        setFieldValue('messageAttachmentContainer', ownContainer, false);
      }
      setFieldValue('extraAttachments', restNames, false);
      return filesToAdd.length;
    }

    // The budget. One file outside the beta keeps the default compression
    // target exactly as before; the plan only runs when several files can
    // share a message.
    let targets = uploads.map(() => null);
    if (maxAttachments > 1) {
      const existingBytes = addMode
        ? await sumAttachmentBytes(existing, (name) => helpers.azure.getImageFileURL(
          name,
          user.id,
          azAccountName,
          STORAGE.sas,
          databaseBeingUsed,
          containerForFile(name, values.messageAttachmentContainer),
          true,
        ))
        : 0;
      const plan = planNewFileTargets({
        newFiles: filesToAdd, existingBytes, totalBytes: mmsTotalBytes,
      });
      if (!plan.ok) {
        dispatch(addNotification('attachment-size', plan.error, NOTIFICATION_STATUS.ERROR));
        return false;
      }
      ({ targets } = plan);
    }

    // Upload to Azure immediately for draft persistence, one file at a time.
    const uploaded = [];
    for (let i = 0; i < uploads.length; i++) {
      // eslint-disable-next-line no-await-in-loop
      const uploadedFilename = await helpers.azure.loadFileToAzureAndGetStatusCode(
        uploads[i],
        null, // No existing attachment
        user.id,
        azAccountName,
        STORAGE.sas,
        databaseBeingUsed,
        ownContainer,
        fileWasNotUpload,
        fileWasNotDelete,
        false,
        'EO-ID',
        true,
        targets[i],
      );
      if (uploadedFilename === null) {
        dispatch(addNotification('file upload', 'Failed to upload file. Please try again.', NOTIFICATION_STATUS.ERROR));
        return false;
      }
      uploaded.push(uploadedFilename);
      originalsRef.current.set(uploadedFilename, uploads[i].file);
    }

    if (addMode) {
      setFieldValue('extraAttachments', [...(values.extraAttachments || []), ...uploaded], false);
      if (!values.messageAttachmentContainer) {
        setFieldValue('messageAttachmentContainer', ownContainer, false);
      }
      return filesToAdd.length;
    }

    // Replace: the first file becomes THE attachment (same sentinel rule as
    // the recorder — see setUploadedAttachment), the rest are extras.
    const [first, ...rest] = uploaded;
    setUploadedAttachment(first, ownContainer);
    setFieldValue('extraAttachments', rest, false);
    return filesToAdd.length;
  };

  /* CON-1930 — the beta's in-progress state around every add. Outside the
   * beta (maxAttachments 1) this is the old call, unchanged. */
  const addFilesToMessage = async (files) => {
    if (maxAttachments <= 1) {
      return addFilesToMessageNow(files);
    }
    const picked = Array.from(files || []).filter(Boolean);
    if (!picked.length) return false;
    if (addingFilesRef.current) {
      dispatch(addNotification('attachment-busy', ATTACHMENTS_BUSY_MESSAGE, NOTIFICATION_STATUS.INFO));
      return false;
    }
    addingFilesRef.current = true;
    const onMessage = [values.attachment, ...(values.extraAttachments || [])].filter(Boolean).length;
    const room = values.attachment && !isVoiceMemo ? maxAttachments - onMessage : maxAttachments;
    setPendingTiles(Math.max(0, Math.min(picked.length, room)));
    try {
      return await addFilesToMessageNow(picked);
    } finally {
      addingFilesRef.current = false;
      setPendingTiles(0);
    }
  };
  const isAddingFiles = pendingTiles > 0;

  const handlePasteEvent = useCallback(async (event) => {
    // Only process if we're not already loading and user has permission
    if (isLoading || !canCreateMessage || isPhonesListEmpty) {
      return;
    }

    // CON-1929 — in the multi-attachment beta a paste is exactly a pick:
    // every image on the clipboard, uncompressed, through addFilesToMessage
    // (limits, budget, rebalance, refusals). Outside the beta the paste below
    // is unchanged: the first image, compressed on its own, as before.
    if (maxAttachments > 1) {
      const pasted = readClipboardImages(event);
      if (!pasted) return;
      if (!pasted.success) {
        dispatch(addNotification('clipboard-paste', pasted.error, NOTIFICATION_STATUS.ERROR));
        return;
      }
      // added is how many files went on, which is fewer than were pasted
      // when the paste runs past the limit (the warning says so).
      const added = await addFilesToMessage(pasted.files);
      if (added) {
        dispatch(addNotification(
          'clipboard-paste',
          added > 1 ? `${added} images pasted successfully!` : 'Image pasted successfully!',
          NOTIFICATION_STATUS.SUCCESS,
        ));
      }
      return;
    }

    const result = await handleClipboardPaste(event, async (uploadFileData) => {
      // CON-1925 — the same path as a picked file: validate, budget, upload,
      // add or replace. Same sentinel rule as the recorder — see
      // setUploadedAttachment.
      const added = await addFilesToMessage([uploadFileData.file]);
      if (added) {
        dispatch(addNotification('clipboard-paste', 'Image pasted successfully!', NOTIFICATION_STATUS.SUCCESS));
      }
    });

    if (result && !result.success && result.error) {
      dispatch(addNotification('clipboard-paste', result.error, NOTIFICATION_STATUS.ERROR));
    }
  }, [isLoading, canCreateMessage, isPhonesListEmpty, dispatch, addFilesToMessage, maxAttachments]);

  const isMac = useMemo(() => isMacPlatform(), []);
  const isTouch = useMemo(() => isTouchDevice(), []);
  const { enterKey, changeEnterKey } = useEnterKeyPreference({
    userKey: user?.salespersonId,
    onSaveError: () => dispatch(addNotification('enter key setting', 'Your Enter key setting wasn\'t saved', NOTIFICATION_STATUS.ERROR)),
  });

  // What Enter does is the consultant's setting (see enterKeyPreference.js):
  // never during IME composition, and always a new line on a touch device.
  const handleKeyDown = (e) => {
    if (shouldSendOnKeyDown(e, enterKey, { isTouch }) && !isLoading && !addingFilesRef.current) {
      if (!values?.message?.trim() && !values.attachment) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      handleSubmit();
    }
  };

  const insertAtCursorPosition = (insertText) => {
    const currentText = values.message;
    // true when messageCursorPosition isn't number maybe false or null
    const isNullMessageCursorPosition = !messageCursorPosition && messageCursorPosition !== 0;
    const text = isNullMessageCursorPosition
      ? `${currentText}${insertText}`
      : `${currentText.substring(0, messageCursorPosition)}${insertText}${currentText.substring(messageCursorPosition)}`;
    const length = !isNullMessageCursorPosition ? messageCursorPosition + insertText.length : text.length;
    setFieldTouched('message', true);
    setFieldValue('message', text, true);
    setMessageCursorPosition(length);
  };

  const handleAddEmoji = (e) => {
    const emoji = e.native;
    insertAtCursorPosition(emoji);
  };

  const handleInsertDynamicField = (field) => {
    insertAtCursorPosition(field);
  };

  const handleInsertSmartLink = (smartLinkToken) => {
    insertAtCursorPosition(smartLinkToken);
  };

  const handleInsertSmartForm = (formToken) => {
    insertAtCursorPosition(formToken);
  };

  const handleInsertTemplate = async (selectedTemplate) => {
    const messageTemplateDetails = helpers.transform.toArray(followUpMessageList).find((item) => item?.id === selectedTemplate);
    const {
      systemGenMessage,
      attachment: messageTemplateDetailsAttachment,
      attachmentContainer: messageTemplateDetailsAttachmentContainer,
    } = messageTemplateDetails || {};

    if (systemGenMessage) {
      const dynamicFieldsObj = {
        calledBy: 'sendMessage',
        screenCalledFrom: 'Chat',
        distShort: false,
        contactId: values?.contactId || null,
      };
      dispatch(replaceSystemGenMessage(systemGenMessage, dynamicFieldsObj));
    }
    // CON-1936 — the template's own files 2..N come with it (beta).
    const templateFiles = templateAttachmentsForApply({ template: messageTemplateDetails, maxAttachments });
    // CON-1934 — in the beta, say so when the template takes files away.
    if (templateDropsAttachments({
      maxAttachments,
      current: [values.attachment, ...(values.extraAttachments || [])],
      templateAttachment: messageTemplateDetailsAttachment || null,
      templateExtras: templateFiles.extras,
    })) {
      dispatch(addNotification('template-attachments', TEMPLATE_REPLACED_ATTACHMENTS_MESSAGE, NOTIFICATION_STATUS.INFO));
    }
    setFieldValue('message', '', true);
    setFieldValue('attachment', messageTemplateDetailsAttachment || '', false);
    setFieldValue('messageAttachmentContainer', messageTemplateDetailsAttachmentContainer || null, false);
    // CON-1925 — a template replaces every attachment, not just the first.
    // CON-1936 — with its own files 2..N when it has them (beta).
    setFieldValue('extraAttachments', templateFiles.extras, false);
    dispatch(setFileUploadToMessage(false));
  };

  const handleOnSelectedFile = async (event) => {
    const { target } = event;
    const { files, value } = target;
    if (!files || !value) {
      return;
    }
    await addFilesToMessage(Array.from(files));
    handleClosePopover();
  };

  const handleChangePhoneNumber = (e) => {
    const selectedPhoneNumber = e.target.value;
    if (!helpers.isEmpty(messagesFilteredByPhone)) {
      dispatch(clearFilteredChatMessages());
    }
    setManuallySelectedNumber(selectedPhoneNumber);
  };

  useEffect(() => {
    if (!isSubmitting && status?.success) {
      handleSetFocusField();
    }
  }, [isSubmitting, status]);

  useEffect(() => {
    if (!helpers.isEmpty(messagesFilteredByPhone)) {
      setManuallySelectedNumber('');
      setFieldValue('number', messagesFilteredByPhone);
    }
  }, [messagesFilteredByPhone]);

  useEffect(() => {
    if (systemGenMessageReplaced) {
      setFieldValue('message', systemGenMessageReplaced, true);
      dispatch(clearSystemGenMessageReplacedFlag());
    }
  }, [dispatch, systemGenMessageReplaced]);

  // Add global paste event listener
  useEffect(() => {
    const handleGlobalPaste = (event) => {
      // Only handle if the message input or its container is focused
      const { activeElement } = document;
      const messageInput = document.getElementsByName('message')[0];

      if (activeElement === messageInput || messageInput?.contains(activeElement)) {
        handlePasteEvent(event);
      }
    };

    document.addEventListener('paste', handleGlobalPaste);

    return () => {
      document.removeEventListener('paste', handleGlobalPaste);
    };
  }, [handlePasteEvent]);

  return (
    <form
      id="connectionMessageForm"
      onSubmit={handleSubmit}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: { xs: '0 16px', md: '0 36px' },
      }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
          >
            {/* Full-width block ABOVE the chip/counter row. Inside that row it
                competed with the character counter for horizontal space and
                squeezed it into an unreadable wrap. */}
            <AiResearchNote
              contactId={contact?.id}
              onDraftApplied={(text) => setFieldValue('message', text)}
              refreshKey={`${researchNoteVersion}:${aiSignalVersion}`}
              onStateChanged={() => setResearchNoteVersion((v) => v + 1)}
            />
            {/* CON-1705 — wraps instead of running off the edge. This row is
                space-between with the AI icon group pinned marginLeft: auto and
                explicitly flexShrink: 0, so everything in it is rigid and the
                "Send to:" block on the left absorbs all the pressure. Measured
                at a 343px content row with the AI icons and the counter
                present: it already overflowed by 47px whenever a voice memo was
                attached, BEFORE this ticket, and the phone badges push that
                further. Wrapping drops the icons and counter to a second line
                on a phone rather than off the screen, and costs nothing at any
                width where the row already fits. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                rowGap: '4px',
                marginY: '8px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                }}
              >
                <Box
                  sx={{
                    marginRight: '12px',
                  }}
                >
                  {children}
                  {/* CON-1705 — the "Send to:" row now appears for a contact
                      with ONE phone too, where it used to collapse entirely and
                      the conversation showed her number nowhere at all. The
                      `contactPhones.length > 1` condition is unchanged and still
                      gates the dropdown; the single-phone case gets a static
                      display in the same slot, never a one-item Select. */}
                  {!helpers.isEmpty(contactPhones) && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        // Wraps too: on the smallest phones (343px content) the
                        // label plus a selector carrying a long country chip is
                        // wider than the whole row, so the label drops to its
                        // own line rather than pushing the chip off the edge.
                        flexWrap: 'wrap',
                        marginTop: '8px',
                      }}
                    >
                      <Typography
                        htmlFor="number"
                        variant="h6"
                        color="textPrimary"
                      >
                        Send to:
                      </Typography>
                      {contactPhones.length > 1 ? (
                        <PhoneSelector
                          id="number"
                          name="number"
                          value={values.number}
                          options={contactPhones}
                          phoneInfoList={contactPhoneInfoList}
                          onChange={handleChangePhoneNumber}
                        />
                      ) : (
                        <SendToPhone
                          phone={values.number || contactPhones[0]}
                          phoneInfo={contactPhoneInfoList[0]}
                        />
                      )}
                    </Box>
                  )}
                </Box>
                {/* CON-1674 — the small persistent reminder. Shown only while
                    a recorded memo is attached, so it sits beside the thing
                    being charged for rather than nagging on an empty box.
                    Same two copy rules as the modal: no figure, no "free". */}
                {!isRecording && isVoiceMemo && values?.attachment && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    Charged at your automated message rate.
                  </Typography>
                )}
                {isRecording && (
                  <Box sx={{ mt: 1 }}>
                    <VoiceRecorder
                      onRecorded={handleVoiceMemoRecorded}
                      onCancel={() => setIsRecording(false)}
                      onError={handleVoiceMemoError}
                    />
                  </Box>
                )}
                {!isRecording && values?.attachment && (
                  <AttachedFilePrewiew
                    fileName={helpers.azure.getImageFileURL(values.attachment, user.id, azAccountName, STORAGE.sas, databaseBeingUsed, values.messageAttachmentContainer)}
                    isLoading={isLoading || isAddingFiles}
                    validateFileInput={validateFileInput}
                    setFileUploadToMessage={updateFileUploadToMessage}
                  />
                )}
                {/* CON-1925 — the 2nd..Nth files, each with its own remove. */}
                {!isRecording && values?.attachment && (values.extraAttachments || []).map((extraName, index) => (
                  <Box
                    key={extraName}
                    sx={{ marginLeft: '8px' }}
                  >
                    <AttachedFilePrewiew
                      fileName={helpers.azure.getImageFileURL(extraName, user.id, azAccountName, STORAGE.sas, databaseBeingUsed, values.messageAttachmentContainer)}
                      isLoading={isLoading || isAddingFiles}
                      validateFileInput={() => true}
                      setFileUploadToMessage={() => removeExtraAttachment(index)}
                    />
                  </Box>
                ))}
                {/* CON-1930 — one placeholder per file still being added. */}
                {!isRecording && Array.from({ length: pendingTiles }, (_, index) => (
                  <Box
                    key={`pending-${index}`}
                    sx={{ marginLeft: values?.attachment || index > 0 ? '8px' : 0 }}
                  >
                    <AttachmentPendingTile />
                  </Box>
                ))}
              </Box>
              {draftSource === 'ai' && !!draftMessage && values.message === draftMessage && (
                <Chip
                  icon={<AutoAwesomeIcon />}
                  label="AI draft"
                  size="small"
                  color="secondary"
                  variant="outlined"
                  sx={{
                    marginRight: '8px',
                  }}
                />
              )}
              {draftSource === 'ai' && !!draftMessage && values.message === draftMessage
                && !!templateChipLabel && (
                <Chip
                  icon={<DescriptionOutlinedIcon />}
                  label={templateChipLabel}
                  title={draftTemplate?.description || draftTemplate?.code}
                  size="small"
                  variant="outlined"
                  sx={{
                    marginRight: '8px',
                    maxWidth: '260px',
                  }}
                />
              )}
              {/* "Look it up" - a labelled chip, deliberately in this row and
                  not in the icon group below: it has to be findable when no
                  draft was prefilled and no research panel is showing, which
                  is exactly the case it exists for. Support account only;
                  renders nothing elsewhere. */}
              <AiLookup
                contactId={contact?.id}
                refreshKey={`${researchNoteVersion}:${aiSignalVersion}`}
                onStateChanged={() => setResearchNoteVersion((v) => v + 1)}
              />
              {/* Grouped and pinned, not distributed. These sat wherever the
                  row's space-between happened to put them, so they moved
                  whenever a chip appeared or the counter changed width - a
                  control that is somewhere different each time is a control
                  she has to find rather than reach for. marginLeft: auto
                  parks the pair against the counter at the right-hand end and
                  keeps them adjacent to each other. */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  marginLeft: 'auto',
                  marginRight: '8px',
                  flexShrink: 0,
                }}
              >
                <AiResearchNoteRestore
                  contactId={contact?.id}
                  refreshKey={`${researchNoteVersion}:${aiSignalVersion}`}
                  onStateChanged={() => setResearchNoteVersion((v) => v + 1)}
                />
                <AiDraftFeedback contactId={contact?.id} />
                <AiOpsNotes />
              </Box>

              <CharacterCounter
                currentLength={(values?.message || '').length}
                maxLength={maxTextMessageLength}
                isError={Boolean(errors?.message)}
                isMobile={isMobile}
              />
              <Zoom
                in={showScrollButton}
                sx={{
                  marginLeft: 'auto',
                  border: '1px solid',
                  position: 'absolute',
                  right: { xs: '16px', md: '36px' },
                  '&:hover': {
                    backgroundColor: '#A1A1F5',
                    color: '#fff',
                  },
                }}
              >
                <IconButton
                  aria-label="scroll messages to bottom"
                  size="small"
                  color="secondary"
                  onClick={scrollMessagesToBottom}
                >
                  <ArrowBackIosNewIcon
                    fontSize="inherit"
                    sx={{
                      transform: 'rotate(-90deg)',
                    }}
                  />
                </IconButton>
              </Zoom>
            </Box>
            <OutlinedInput
              name="message"
              error={Boolean(errors?.message)}
              color="secondary"
              placeholder="Write something..."
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onBlur={saveMessageCursorPosition}
              value={values.message}
              disabled={isPhonesListEmpty || isLoading || !canCreateMessage}
              size="small"
              maxRows={6}
              startAdornment={(
                <InputStartAdornment
                  isLoading={isLoading || isAddingFiles}
                  isAdditionalActionsDisabled={isPhonesListEmpty || !canCreateMessage}
                  isScheduleDisabled={Boolean(errors?.message) || (!values.message?.trim() && !values.attachment)}
                  handleOpenPopover={handleOpenPopover}
                  handleClosePopover={handleClosePopover}
                  popoverAnchorEl={popoverAnchorEl}
                  openUploadDialog={handleOpenAttachFile}
                  openScheduleDialog={handleOpenScheduledModal}
                  openDynamicFieldsDialog={handleOpenDynamicFieldModal}
                  openSmartLinksDialog={canViewSmartLinks ? handleOpenSmartLinkModal : null}
                  openSmartFormsDialog={canViewSmartForms ? handleOpenSmartFormModal : null}
                  openCpPageDialog={canViewSmartForms ? handleOpenCpPageModal : null}
                  openMessageTemplateDialog={handleOpenMessageTemplateModal}
                  onAttachContactCard={handleAttachContactCard}
                  onRecordVoiceMemo={startVoiceMemo}
                  canRecordVoiceMemo={isRecordingSupported() && voiceMemoAcknowledged !== null
                    && !(values.extraAttachments || []).length}
                  textingProvider={user?.textingProvider}
                  hasAttachment={!!values.attachment}
                  isContactCardAttached={values.attachment?.includes(VCARD_FILENAME)}
                  canAddAnotherFile={maxAttachments > 1 && !isVoiceMemo
                    && [values.attachment, ...(values.extraAttachments || [])].filter(Boolean).length < maxAttachments}
                />
              )}
              endAdornment={!isMobile && (
                <InputEndAdornment
                  isLoading={isLoading}
                  isAddEmojiDisabled={isPhonesListEmpty || !canCreateMessage}
                  handleAddEmoji={handleAddEmoji}
                  handleSetFocusField={handleSetFocusField}
                />
              )}
              sx={{
                paddingLeft: '8px',
                backgroundColor: isMessageIncludesBlanks ? STYLE_OPTIONS.COLOR_LIGHT_GREY : undefined,
              }}
              autoFocus
              fullWidth
              multiline
            />
            <input {...getInputProps({ onChange: handleOnSelectedFile })} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              marginLeft: '16px',
            }}
          >
            <TooltipMUI6Container
              tooltipProps={{
                title: 'You have reached your daily text message limit.',
              }}
              disabled={!isSMSSendingDisabled}
            >
              <Button
                type="submit"
                disabled={(!values.message?.trim() && !values.attachment) || isLoading || isAddingFiles || !canCreateMessage || Boolean(errors?.message) || isSMSSendingDisabled}
                sx={{
                  ...STYLE_OPTIONS.BUTTON_PRIMARY,
                  height: '38px',
                  fontWeight: 500,
                }}
              >
                Send
              </Button>
            </TooltipMUI6Container>
            <Box sx={{ marginLeft: '4px', marginTop: '3px' }}>
              <EnterKeySettings
                enterKey={enterKey}
                isMac={isMac}
                onChange={changeEnterKey}
                onClosed={handleSetFocusField}
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            minHeight: '24px',
            marginTop: '4px',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ marginRight: '16px' }}
          >
            {getEnterKeyHint(enterKey, { isMac, isTouch })}
          </Typography>
          {isPhonesListEmpty && (
            <>
              <ErrorIcon
                sx={{
                  marginRight: '4px',
                  fontSize: '16px',
                  color: '#F15642',
                }}
              />
              <Typography
                variant="caption"
                color="#F15642"
              >
                The contact does not have a phone number.
              </Typography>
            </>
          )}
          {isMessageIncludesBlanks && (
            <Box
              sx={{
                display: 'flex',
                color: '#F15642',
              }}
            >
              <Typography
                variant="h6"
              >
                Please review!&nbsp;&nbsp;&nbsp;Includes blanks!
              </Typography>
              <HelpCircleMUI6
                showInfo={(
                  <>
                    This may have happened because you used a template that has dynamic field(s) that cannot be used on this screen.
                    <br />
                    <br />
                    Review the blanks & fill them in, or use a different template.
                  </>
                )}
              />
            </Box>
          )}
        </Box>
      </Box>
      {/* CON-1674 — shown once, the first time this consultant records a memo
          in chat. Everything sent from Connections has been free, so the
          charge is genuinely new information here and is stated plainly. */}
      <ModalDialogMUI6
        isVisible={showVoiceMemoDisclosure}
        title="About voice memos"
        okText="Record"
        closeText="Cancel"
        onOk={confirmVoiceMemoDisclosure}
        onClose={() => setShowVoiceMemoDisclosure(false)}
        content={(
          <Box>
            {/* NO DOLLAR FIGURE, deliberately. Consultants are on different
                per-text rates — $0.025 among them today, and more tiers are
                possible — so a number here would be wrong for some users on
                day one and would go stale silently without anyone noticing.
                Pointing at their own automated rate is correct for everyone.

                NO "FREE", also deliberately, and a standing rule for CP copy:
                everything else from Connections is included in a subscription
                they already pay for. Calling it free invites the question of
                what else is not. */}
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Sending a voice memo is charged at the same per-text rate as your
              automated messages. Everything else you send from Connections is
              included with your subscription.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Memos can be up to 2 minutes. You&apos;ll only see this once.
            </Typography>
          </Box>
        )}
      />
      <ModalScheduleMessage
        isModalVisible={scheduleModalVisible}
        header="Set Scheduled Date & Time"
        onCloseModal={handleCloseScheduledModal}
        formik={formik}
        {...isDailySMSSendingDisabled ? {
          scheduledDateTimeMinISO: serverTimeWithWait.clone().local().toISOString()
        } : {}}
      />
      <ModalInsertDynamicFields
        isModalVisible={insertDynamicModalVisible}
        dynamicFields={dynamicFields}
        onInsert={handleInsertDynamicField}
        onCloseModal={handleCloseDynamicFieldModal}
      />
      <ModalApplyTemplate
        isModalVisible={applyTemplateModalVisible}
        followUpMessages={followUpMessages}
        onApply={handleInsertTemplate}
        onCloseModal={handleCloseMessageTemplateModal}
      />
      <ModalInsertSmartLink
        isModalVisible={insertSmartLinkModalVisible}
        onInsert={handleInsertSmartLink}
        onCloseModal={handleCloseSmartLinkModal}
      />
      <ModalInsertSmartForm
        isModalVisible={insertSmartFormModalVisible}
        onInsert={handleInsertSmartForm}
        onCloseModal={handleCloseSmartFormModal}
      />
      <ModalInsertSmartForm
        cpPageMode
        isModalVisible={insertCpPageModalVisible}
        onInsert={handleInsertSmartForm}
        onCloseModal={handleCloseCpPageModal}
      />
    </form>
  );
}

MessageAdd.propTypes = {
  chat: PropTypes.object.isRequired,
  sendingLimits: PropTypes.object.isRequired,
  maxTextMessageLength: PropTypes.number.isRequired,
  scrollMessagesToBottom: PropTypes.func.isRequired,
  setShowScheduledMessages: PropTypes.func.isRequired,
  children: PropTypes.node,
  showScrollButton: PropTypes.bool,
  userRolePermissions: PropTypes.array,
};

export default MessageAdd;
